'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useHaptic } from '../../hooks/useHaptic';

export type CratePhase = 'idle' | 'closing' | 'rolling' | 'opening' | 'reveal';

export interface CrateRollScale {
  lo: number;
  hi: number;
}

interface PlayParams {
  value: number;
  preview: { minValue: number; maxValue: number } | null;
  isSuccessful: boolean;
}

// WHY: тайминги 1180/280/1180/820 связаны с crateLidCover/crateLidUncover (1150 мс) в ContractScreen.css — менять вместе
export const useCrateRoll = (gameMotionEnabled: boolean) => {
  const { impact, notification } = useHaptic();

  const [phase, setPhase] = useState<CratePhase>('idle');
  const [needleT, setNeedleT] = useState(0.5);
  const [scale, setScale] = useState<CrateRollScale | null>(null);
  const [animating, setAnimating] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const reset = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setAnimating(false);
    setPhase('idle');
    setNeedleT(0.5);
    setScale(null);
  }, []);

  const play = useCallback(
    async ({ value, preview, isSuccessful }: PlayParams) => {
      const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const lo = Math.min(preview?.minValue ?? 0, value);
      const hi = Math.max(preview?.maxValue ?? 0, value);
      setScale({ lo, hi });
      const toT = (v: number) => (hi > lo ? Math.min(Math.max((v - lo) / (hi - lo), 0), 1) : 0.5);
      const finalT = toT(value);

      if (!gameMotionEnabled) {
        setNeedleT(finalT);
        if (isSuccessful) notification('success');
        else notification('error');
        await wait(300);
        return;
      }

      setAnimating(true);
      setPhase('closing');
      await wait(1180);
      setPhase('rolling');
      impact('medium');
      await new Promise<void>((resolve) => {
        const halfSweeps = 12;
        const duration = 6000;
        const start = performance.now();
        const easeOut = (x: number) => 1 - (1 - x) * (1 - x);
        const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
        const clamp = (x: number) => Math.max(0, Math.min(1, x));
        const tick = (now: number) => {
          const p = Math.min((now - start) / duration, 1);
          if (p >= 1) {
            setNeedleT(finalT);
            resolve();
            return;
          }
          const ampTaper = p < 0.55 ? 1 : 1 - (p - 0.55) / 0.45;
          const amp = 0.5 * ampTaper;
          const cTaper = p < 0.7 ? 0 : easeInOut((p - 0.7) / 0.3);
          const center = 0.5 + (finalT - 0.5) * cTaper;
          const phaseAngle = Math.PI / 2 + Math.PI * halfSweeps * easeOut(p);
          setNeedleT(clamp(center + amp * Math.cos(phaseAngle)));
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      });
      await wait(280);
      setPhase('opening');
      await wait(1180);
      setPhase('reveal');
      if (isSuccessful) notification('success');
      else notification('error');
      await wait(820);
    },
    [gameMotionEnabled, impact, notification],
  );

  return { phase, needleT, scale, animating, play, reset };
};
